import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { compareObjects, useAlert } from '@schamburg-web/components';
import React from 'react';
import { useLoginContext } from '../../../contexts/LoginContext';
import { EmailFormatSelector } from '../shared/components/EmailFormatSelector';
import { stepperStyles } from '../shared/styles';
import { updateUser } from '../util';
import ChangePasswordDialog from './ChangePasswordDialog';

const notificationTitle = 'Email Type';
const notificationsSubTitle = 'What type of email do you want to receive?';

const ManageAccount: React.FC = () => {
  const [openPwdDialog, setOpenPwdDialog] = React.useState(false);
  const { user, setUser, initalized } = useLoginContext();
  const { showAlert } = useAlert();
  const [originalUser, setOriginalUser] = React.useState<typeof user | null>(
    null
  );
  const [isFormInitialized, setIsFormInitialized] = React.useState(false);

  React.useEffect(() => {
    if (initalized && !isFormInitialized) {
      setOriginalUser(JSON.parse(JSON.stringify(user)));
      setIsFormInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initalized, isFormInitialized]); // Intentionally not including 'user' to prevent unnecessary re-runs

  const theme = useTheme();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    setUser({ ...user, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const checked = e.target.checked;
    setUser({ ...user, [name]: checked });
  };

  const compareUserFields = (
    currentUser: typeof user,
    originalUserData: typeof originalUser
  ) => {
    return compareObjects(currentUser, originalUserData, [
      'name',
      'contentType',
      'primary',
      'profile',
    ]);
  };

  const hasChanges = () => {
    if (!originalUser || !isFormInitialized) return false;
    const changes = compareUserFields(user, originalUser);
    return changes.length > 0;
  };

  const formHasChanges = hasChanges();

  const cancel = () => {
    if (originalUser) {
      // Reset user to original state
      setUser(originalUser);
    }
  };

  const submit = async () => {
    try {
      const exception = await updateUser(user);
      if (exception === '') {
        // Update successful, set the new state as original
        setOriginalUser(JSON.parse(JSON.stringify(user)));
        showAlert('Account updated successfully!', 'success');
      } else {
        // Handle error - show error message to user
        showAlert(`Error updating account: ${exception}`, 'error');
      }
    } catch (error) {
      showAlert(
        'An unexpected error occurred while updating your account.',
        'error'
      );
      console.error('Error updating user:', error);
    }
  };

  return (
    <Box sx={stepperStyles.stepperContentArea}>
      <Box sx={stepperStyles.stepperContentInner}>
        <Paper elevation={1} sx={stepperStyles.stepperContentPaper(theme)}>
          <Box sx={stepperStyles.largeContainer}>
            <Box sx={stepperStyles.centeredContent}>
              <Typography
                variant="h5"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                }}
              >
                Manage Account
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 3,
                }}
              >
                Update your account information and email preferences
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenPwdDialog(true)}
                sx={{ textTransform: 'none' }}
              >
                Change Password
              </Button>
            </Box>

            <Paper elevation={3} sx={stepperStyles.elevatedCard(theme)}>
              <form autoComplete="off" noValidate>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      mb: 2,
                    }}
                  >
                    Account Information
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      label="Name"
                      name="name"
                      onChange={handleChange}
                      required
                      value={user.name || ''}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Box sx={{ textAlign: 'left' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="primary"
                            color="primary"
                            checked={user.primary || false}
                            onChange={handleCheckboxChange}
                          />
                        }
                        label="Use Primary Email"
                        sx={{ display: 'block', mb: 1 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="profile"
                            color="primary"
                            checked={user.profile || false}
                            onChange={handleCheckboxChange}
                          />
                        }
                        label="Use Additional Email Addresses"
                        sx={{ display: 'block' }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 4 }}>
                  <EmailFormatSelector
                    value={user.contentType || 'text/html'}
                    onChange={handleChange}
                    name="contentType"
                    title={notificationTitle}
                    subtitle={notificationsSubTitle}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'center',
                    pt: 1,
                  }}
                >
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={!formHasChanges}
                    onClick={submit}
                    sx={{
                      textTransform: 'none',
                      minWidth: '120px',
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={!formHasChanges}
                    onClick={cancel}
                    sx={{
                      textTransform: 'none',
                      minWidth: '120px',
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </form>
            </Paper>
          </Box>
        </Paper>
      </Box>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={openPwdDialog}
        onClose={() => setOpenPwdDialog(false)}
      />
    </Box>
  );
};

export default ManageAccount;
